import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Phone, Menu, X, ChevronDown, Paintbrush, Hammer, Wrench, Building, Home as HomeIcon, Mail, Star, MessageCircle } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navigation = [
    { name: 'Home', path: '/', icon: HomeIcon },
    { 
      name: 'Leistungen', 
      path: '/leistungen',
      icon: Hammer,
      submenu: [
        { name: 'Immobilien', path: '/leistungen/immobilien', icon: Building },
        { name: 'Malerarbeiten', path: '/leistungen/malerarbeiten', icon: Paintbrush },
        { name: 'Bodenbeläge', path: '/leistungen/bodenbelaege', icon: Paintbrush },
        { name: 'Fliesen & Naturstein', path: '/leistungen/fliesen-naturstein', icon: Wrench },
        { name: 'Gipserarbeiten', path: '/leistungen/gipserarbeiten', icon: Wrench },
        { name: 'Schalung & Beton', path: '/leistungen/schalungsarbeiten', icon: Building },
        { name: 'Trockenbau', path: '/leistungen/trockenbau', icon: Building },
        { name: 'Fassaden', path: '/leistungen/fassaden', icon: Building },
        { name: 'Türen & Fenster', path: '/leistungen/tueren-fenster', icon: Building },
        { name: 'Schimmel & Risse', path: '/leistungen/schimmel-riss', icon: Wrench },
        { name: 'Pflaster & Dach', path: '/leistungen/pflaster-dach', icon: Wrench },
      ]
    },
    { name: 'Über uns', path: '/uber-uns', icon: Building },
    { name: 'Kontakt', path: '/kontakt', icon: Mail },
  ];

  return (
    <>
      <motion.header
        initial={{ y: -100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          isScrolled 
            ? 'bg-[#003B7A]/95 backdrop-blur-xl shadow-2xl' 
            : 'bg-[#003B7A]/90 backdrop-blur-2xl'
        }`}
        style={{
          boxShadow: isScrolled 
            ? '0 8px 32px rgba(0, 59, 122, 0.4), 0 2px 8px rgba(255, 215, 0, 0.3)' 
            : 'none',
          borderBottom: isScrolled ? '1px solid rgba(255, 215, 0, 0.2)' : 'none'
        }}
        data-testid="main-header"
      >
        <div className="max-w-[1400px] mx-auto px-4 md:px-8">
          <div className="flex items-center justify-between h-20">
            {/* Logo with company name */}
            <Link to="/" className="flex items-center gap-3 group" data-testid="header-logo-link">
              <motion.div
                whileHover={{ scale: 1.05, rotateY: 5 }}
                transition={{ type: "spring", stiffness: 300 }}
                className="transform-3d"
              >
                <img 
                  src="https://customer-assets.emergentagent.com/job_cazis-bauexperte/artifacts/ivljyg63_890dbcbe-dfaa-42cf-b225-c992d1d7a016.JPG" 
                  alt="Marali Immobilien AG & Baufirma"
                  className="h-12 md:h-14 w-auto transition-all duration-300 drop-shadow-lg group-hover:drop-shadow-2xl"
                  data-testid="company-logo"
                  style={{
                    filter: 'drop-shadow(0 4px 12px rgba(255, 215, 0, 0.3))'
                  }}
                />
              </motion.div>
            </Link>

            {/* Desktop Navigation with Icons */}
            <nav className="hidden lg:flex items-center gap-6" data-testid="desktop-navigation">
              {navigation.map((item) => {
                const Icon = item.icon;
                return (
                  <div key={item.name} className="relative group">
                    {item.submenu ? (
                      <>
                        <motion.button 
                          className="flex items-center gap-2 font-body font-semibold text-sm tracking-wide text-white hover:text-[#FFD700] cursor-pointer transition-all duration-300 py-2 px-3 rounded-lg hover:bg-white/5"
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                        >
                          <Icon className="w-4 h-4" />
                          {item.name}
                          <ChevronDown className="w-4 h-4" />
                        </motion.button>
                        <div 
                          className="absolute top-full left-0 pt-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-[999]"
                          style={{ pointerEvents: 'auto' }}
                        >
                          <motion.div 
                            initial={{ y: -10, opacity: 0 }}
                            animate={{ y: 0, opacity: 1 }}
                            className="w-72 bg-black/95 backdrop-blur-xl border border-[#FFD700]/30 py-2 shadow-2xl"
                            style={{
                              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5), 0 0 40px rgba(255, 215, 0, 0.2)'
                            }}
                          >
                            <div className="absolute top-0 left-0 w-1 h-full bg-gradient-to-b from-[#FFD700] to-[#FFC700]"></div>
                            {item.submenu.map((subItem) => {
                              const SubIcon = subItem.icon;
                              return (
                                <Link
                                  key={subItem.path}
                                  to={subItem.path}
                                  className="flex items-center gap-3 px-6 py-3.5 text-sm font-body text-gray-300 hover:bg-[#FFD700]/10 hover:text-[#FFD700] transition-all duration-200 group/item"
                                  data-testid={`submenu-${subItem.path}`}
                                >
                                  <SubIcon className="w-4 h-4 group-hover/item:scale-110 transition-transform" />
                                  <span>{subItem.name}</span>
                                </Link>
                              );
                            })}
                          </motion.div>
                        </div>
                      </>
                    ) : (
                      <Link
                        to={item.path}
                        className={`flex items-center gap-2 font-body font-semibold text-sm tracking-wide transition-all duration-300 py-2 px-3 rounded-lg hover:bg-white/5 ${
                          location.pathname === item.path ? 'text-[#FFD700]' : 'text-white hover:text-[#FFD700]'
                        }`}
                        data-testid={`nav-${item.path}`}
                      >
                        <Icon className="w-4 h-4" />
                        {item.name}
                      </Link>
                    )}
                  </div>
                );
              })}
            </nav>

            {/* Right side CTAs Ultra Premium */}
            <div className="hidden lg:flex items-center gap-3">
              <motion.a 
                href="tel:+41792649063" 
                className="flex items-center gap-2 font-body font-bold text-sm text-white hover:text-[#FFD700] transition-all duration-300 px-4 py-2.5 rounded-lg hover:bg-white/5 border border-white/10 hover:border-[#FFD700]/50"
                whileHover={{ scale: 1.05, y: -2 }}
                whileTap={{ scale: 0.95 }}
                data-testid="header-phone-link"
                style={{
                  boxShadow: '0 4px 15px rgba(255, 215, 0, 0.1)'
                }}
              >
                <Phone className="w-4 h-4" />
                <span>+41 79 264 90 63</span>
              </motion.a>
              
              <motion.a
                href="https://wa.me/41792649063"
                target="_blank"
                rel="noopener noreferrer"
                className="relative flex items-center gap-2 bg-gradient-to-r from-[#25D366] to-[#20BA59] text-white px-5 py-2.5 text-xs font-bold uppercase tracking-widest rounded-lg overflow-hidden group"
                whileHover={{ scale: 1.05, y: -2 }}
                whileTap={{ scale: 0.95 }}
                data-testid="header-whatsapp-button"
                style={{
                  boxShadow: '0 4px 20px rgba(37, 211, 102, 0.4)'
                }}
              >
                <MessageCircle className="w-4 h-4 relative z-10" />
                <span className="relative z-10">WhatsApp</span>
                <div className="absolute inset-0 bg-white opacity-0 group-hover:opacity-20 transition-opacity duration-300"></div>
              </motion.a>
              
              <motion.a
                href="https://www.google.com/maps/search/?api=1&query=Marali+Immobilien+AG+Baufirma+Cazis"
                target="_blank"
                rel="noopener noreferrer"
                className="relative flex items-center gap-2 bg-white text-black px-5 py-2.5 text-xs font-bold uppercase tracking-widest rounded-lg hover:bg-gray-100 transition-all duration-300"
                whileHover={{ scale: 1.05, y: -2 }}
                whileTap={{ scale: 0.95 }}
                data-testid="header-google-button"
                style={{
                  boxShadow: '0 4px 15px rgba(255, 255, 255, 0.2)'
                }}
              >
                <svg className="w-5 h-5" viewBox="0 0 48 48">
                  <path fill="#FFC107" d="M43.611,20.083H42V20H24v8h11.303c-1.649,4.657-6.08,8-11.303,8c-6.627,0-12-5.373-12-12c0-6.627,5.373-12,12-12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C12.955,4,4,12.955,4,24c0,11.045,8.955,20,20,20c11.045,0,20-8.955,20-20C44,22.659,43.862,21.35,43.611,20.083z"/>
                  <path fill="#FF3D00" d="M6.306,14.691l6.571,4.819C14.655,15.108,18.961,12,24,12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C16.318,4,9.656,8.337,6.306,14.691z"/>
                  <path fill="#4CAF50" d="M24,44c5.166,0,9.86-1.977,13.409-5.192l-6.19-5.238C29.211,35.091,26.715,36,24,36c-5.202,0-9.619-3.317-11.283-7.946l-6.522,5.025C9.505,39.556,16.227,44,24,44z"/>
                  <path fill="#1976D2" d="M43.611,20.083H42V20H24v8h11.303c-0.792,2.237-2.231,4.166-4.087,5.571c0.001-0.001,0.002-0.001,0.003-0.002l6.19,5.238C36.971,39.205,44,34,44,24C44,22.659,43.862,21.35,43.611,20.083z"/>
                </svg>
                <div className="flex items-center gap-1">
                  <span>5.0</span>
                  <Star className="w-4 h-4 fill-[#FFD700] text-[#FFD700]" />
                </div>
              </motion.a>
              
              <motion.div
                whileHover={{ scale: 1.05, y: -2 }}
                whileTap={{ scale: 0.95 }}
              >
                <Link
                  to="/kontakt"
                  className="relative inline-flex items-center gap-2 bg-gradient-to-r from-[#FFD700] via-[#FFC700] to-[#FFD700] text-[#003B7A] px-6 py-3 text-xs font-bold uppercase tracking-widest rounded-lg transition-all duration-300 shadow-gold"
                  data-testid="header-cta-button"
                  style={{
                    boxShadow: '0 4px 30px rgba(255, 215, 0, 0.5), 0 0 60px rgba(255, 215, 0, 0.3)'
                  }}
                >
                  <Mail className="w-4 h-4" />
                  <span className="relative z-10">Angebot anfordern</span>
                </Link>
              </motion.div>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="lg:hidden bg-[#003B7A] text-white p-3 rounded-lg shadow-lg"
              data-testid="mobile-menu-toggle"
            >
              {isMobileMenuOpen ? <X className="w-7 h-7" /> : <Menu className="w-7 h-7" />}
            </button>
          </div>
        </div>
      </motion.header>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, x: '100%' }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: '100%' }}
            transition={{ type: 'tween', duration: 0.3 }}
            className="fixed inset-0 z-40 bg-white lg:hidden"
            style={{ top: '80px' }}
            data-testid="mobile-menu"
          >
            <div className="flex flex-col h-full overflow-y-auto">
              <nav className="flex-1 px-4 py-6 space-y-2">
                {navigation.map((item) => (
                  <div key={item.name}>
                    {item.submenu ? (
                      <>
                        <div className="font-body font-semibold text-base py-3 border-b border-gray-200">
                          {item.name}
                        </div>
                        {item.submenu.map((subItem) => (
                          <Link
                            key={subItem.path}
                            to={subItem.path}
                            onClick={() => setIsMobileMenuOpen(false)}
                            className="block pl-4 py-2 text-sm font-body text-gray-600 hover:text-[#FFD700]"
                            data-testid={`mobile-submenu-${subItem.path}`}
                          >
                            {subItem.name}
                          </Link>
                        ))}
                      </>
                    ) : (
                      <Link
                        to={item.path}
                        onClick={() => setIsMobileMenuOpen(false)}
                        className="block font-body font-semibold text-base py-3 border-b border-gray-200 hover:text-[#FFD700]"
                        data-testid={`mobile-nav-${item.path}`}
                      >
                        {item.name}
                      </Link>
                    )}
                  </div>
                ))}
              </nav>
              <div className="p-4 space-y-3 border-t border-gray-200">
                <a
                  href="tel:+41792649063"
                  className="flex items-center justify-center gap-2 w-full bg-[#003B7A] text-white py-3 font-bold text-sm"
                  data-testid="mobile-phone-button"
                >
                  <Phone className="w-5 h-5" />
                  +41 79 264 90 63
                </a>
                <a
                  href="https://wa.me/41792649063"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block w-full bg-[#25D366] text-white py-3 text-center font-bold text-sm"
                  data-testid="mobile-whatsapp-button"
                >
                  WhatsApp schreiben
                </a>
                <Link
                  to="/kontakt"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="block w-full bg-[#FFD700] text-white py-3 text-center font-bold text-sm"
                  data-testid="mobile-cta-button"
                >
                  Angebot anfordern
                </Link>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Header;